import { USER_ROLES, UserRole } from './types/UserRole';

// The one declaration of who may perform each kind of write. Both sides of every write read it:
// dashboard-api turns a capability into an `ensureRoleIn` allow-list, and the dashboard turns the
// same capability into the `disabledFor` deny-list on the control that calls the route.
//
// It lives here, and not in either of them, because the pair used to be written two times — a
// server allow-list in services/dashboard-api/lib/validators.ts and a UI literal in the component
// — with nothing tying the two together. A reviewer had to compare two literals by eye, and when
// they disagreed the UI hid an action the route still permitted. That is the Broken Function Level
// Authorization class of COR-837 and COR-838.
//
// Capabilities, not one entry per route. The 24 gated dashboard-api routes state three distinct
// role decisions between them, so a per-route table would write those three facts 24 times and
// give a reviewer 24 literals to compare instead of three. Which capability each route needs is
// recorded per route in services/dashboard-api/test/app-role-gates.spec.ts, which is where an
// exhaustive per-route audit belongs.
//
// Add a capability rather than widen one when a new surface does not mean the same thing as an
// existing one, even if the role set is the same today: two capabilities that happen to agree can
// later disagree, but a shared entry cannot be split after every call site has adopted it.
export type RoleCapability = 'contentWrite' | 'billingWrite' | 'tokenWrite';

// Frozen, not only typed `readonly`: the same array object reaches every route registration and
// every control, so one accidental `push` would widen a live authorization gate everywhere at once.
export const CAPABILITY_ROLES: { readonly [C in RoleCapability]: readonly UserRole[] } = Object.freeze({
  // Cache manager, scheduled rendering, URL parameters, ignored URLs and the General settings tab.
  contentWrite: Object.freeze<UserRole[]>(['superAdmin', 'admin', 'teamMember']),
  // Plan, payment, invoices and the ChargeBee billing portal.
  billingWrite: Object.freeze<UserRole[]>(['superAdmin', 'admin', 'billingManager']),
  // Rotating the account-wide Prerender API token, which billingManager may not do either.
  tokenWrite: Object.freeze<UserRole[]>(['superAdmin', 'admin']),
});

// The complement, for the dashboard, whose controls take the roles to REFUSE. Derived rather than
// declared, so a role added to `USER_ROLES` is refused by the UI on the same edit that leaves it
// out of the allow-list, and the two directions cannot state different things.
export const rolesRefusedBy = (capability: RoleCapability): readonly UserRole[] =>
  Object.freeze(USER_ROLES.filter((role) => !CAPABILITY_ROLES[capability].includes(role)));
